import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { setSidePanel, closeSidePanel } from 'reducers/sidePanel/actions';
import { toggleAccountMenuDropdown } from 'reducers/customer/actions';

import CurveThumbnail from 'components/CurveThumbnail';
import { CurveThumbnailContainer } from 'components/PanelComponents';
import { default as HasRoleSubmenu } from 'components/Submenu';
import Spinner from 'components/Spinner';

import {
  API_FIELD_MAPPINGS,
  RESOURCE_TYPES,
  SUBMENU_TYPES,
  WRITE_ACCESS_AUTH_ROLES,
  SIDEPANEL_STATES,
  defaultItemsToDisplayInSingleRow,
  defaultRowHeight,
  defaultSecondaryRowHeight
} from 'utils/appConstants';
import { durationFormatter, isExternallyManaged } from 'utils/format';
import {
  subscribe,
  POLL_CONTROLS_AND_RESPONSES,
  unsubscribe,
  POLL_DELETED_CONTROLS_AND_RESPONSES,
  POLL_CURVES
} from 'reducers/polling/actions';
import { FlexPage } from 'components/CommonStyles';
import { DATE_FORMAT } from 'utils/appConstants';
import Table from 'components/Table';
const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const Submenu = HasRoleSubmenu(WRITE_ACCESS_AUTH_ROLES);

const ControlsPage = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  overflow-y: auto;
`;
const Controls = () => {
  const dispatch = useDispatch();
  const controls = useSelector(state => state.control.controls);
  const deletedControls = useSelector(state => state.control.deletedControls);
  const curves = useSelector(state => state.curve.curves);

  const showAcctMenuDropdown = useSelector(
    state => state.customer.showAcctMenuDropdown
  );
  const [showSubmenuDropdown, setShowSubmenuDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [controlsReadableData, setControlsReadableData] = useState([]);
  const [deletedControlsReadableData, setDeletedControlsReadableData] =
    useState([]);
  const [curvesReadableData, setCurvesReadableData] = useState([]);
  const subName = 'Controls';
  const activeControlsTableRef = useRef(null);
  const expiredControlsTableRef = useRef(null);
  const curvesTableRef = useRef(null);
  const sidePanelIsOpen = useSelector(
    state => state.sidePanel.displaySidePanel
  );

  useEffect(() => {
    dispatch(
      subscribe({
        subscriber: subName,
        pollTypes: [
          {
            type: POLL_CONTROLS_AND_RESPONSES
          },
          {
            type: POLL_DELETED_CONTROLS_AND_RESPONSES
          },
          {
            type: POLL_CURVES
          }
        ],
        pollRate
      })
    );

    createReadableControlData(controls);
    createReadableControlData(deletedControls, true);
    createReadableCurveData(curves);
    setIsLoading(false);
    return () => {
      dispatch(unsubscribe(subName));
    };
  }, []);

  useEffect(() => {
    createReadableControlData(controls);
  }, [controls]);

  useEffect(() => {
    createReadableControlData(deletedControls, true);
  }, [deletedControls]);

  useEffect(() => {
    createReadableCurveData(curves);
  }, [curves]);

  const controlColumnDefs = useCallback(isDeletedControls => {
    const columns = [
      {
        headerName: 'Control Id',
        field: 'controlId'
      },
      {
        headerName: 'Description',
        field: 'description'
      },
      {
        headerName: 'Control Mode',
        field: 'controlTypesList',
        cellRenderer: ({ value }) => {
          if (!value) {
            return '';
          }
          const controlTypesDisplayList =
            value.length > defaultItemsToDisplayInSingleRow
              ? value.slice(0, defaultItemsToDisplayInSingleRow).concat(['...'])
              : value;
          return (
            <ul>
              {controlTypesDisplayList.map(controlType => (
                <li key={controlType}>{controlType}</li>
              ))}
            </ul>
          );
        }
      },
      {
        headerName: 'Associated Node',
        field: 'nodeId'
      }
    ];
    if (externallyManagedEdevs) {
      columns.push({
        headerName: 'Remotely Managed',
        field: 'localControl',
        cellRenderer: ({ value }) => isExternallyManaged(value),
        maxWidth: 120
      });
    }
    columns.push(
      {
        headerName: 'Start Time',
        field: 'start',
        cellRenderer: ({ value }) => (
          <div>{moment.unix(value).format(DATE_FORMAT)}</div>
        )
      },
      {
        headerName: 'End Time',
        field: 'end',
        cellRenderer: ({ value }) => (
          <div>{moment.unix(value).format(DATE_FORMAT)}</div>
        )
      },
      {
        headerName: 'Duration',
        field: 'duration',
        cellRenderer: ({ value }) => (
          <div>{durationFormatter(parseFloat(value) * 100)}</div>
        )
      }
    );
    if (!isDeletedControls) {
      columns.push({
        headerName: 'Status',
        field: 'statusDisplayValue'
      });
    }
    return columns;
  }, []);

  const curveColumnDefs = useCallback(() => {
    const columns = [
      {
        headerName: 'Curve',
        field: 'curve',
        maxWidth: 90,
        cellRenderer: row => (
          <CurveThumbnailContainer>
            <CurveThumbnail curveData={row.data.curveDataArray} />
          </CurveThumbnailContainer>
        )
      },
      {
        headerName: 'Curve ID',
        field: 'curveId',
        maxWidth: 120
      }
    ];
    if (externallyManagedEdevs) {
      columns.push({
        headerName: 'Remotely Managed',
        field: 'localCurve',
        cellRenderer: ({ value }) => isExternallyManaged(value),
        maxWidth: 100
      });
    }
    columns.push({
      headerName: 'Curve Type',
      field: 'curveTypeDisplayValue'
    });
    return columns;
  }, []);

  const createReadableControlData = (
    controls = [],
    isDeletedControls = false
  ) => {
    const controlsReadableData = controls.map(control => {
      const { controlTypes = {} } = control;
      const controlTypesList = Object.keys(controlTypes).sort();

      //TODO: Once backend handles having 'Expired' controls, take away this conditional.
      //For now, just don't show status.
      const end = control.start + control.duration;
      let statusDisplayValue = '';
      if (moment().unix() < end) {
        statusDisplayValue =
          API_FIELD_MAPPINGS.CONTROL_STATUS_DISPLAYS.type[control.status.value]
            .displayText;
      }

      return {
        ...control,
        controlTypesList,
        statusDisplayValue,
        end
      };
    });

    if (isDeletedControls) {
      setDeletedControlsReadableData(controlsReadableData);
    } else {
      setControlsReadableData(controlsReadableData);
    }
  };

  const createReadableCurveData = (curves = []) => {
    const curvesReadableData = curves.map(obj => {
      const curveTypeDisplayValue =
        API_FIELD_MAPPINGS.CURVE.type[obj.curveType].displayText;

      return {
        ...obj,
        curveTypeDisplayValue
      };
    });

    setCurvesReadableData(curvesReadableData);
  };

  const onControlSelected = useCallback(
    row => {
      const { controlId } = row.data;
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.CONTROL,
          id: controlId,
          state: SIDEPANEL_STATES.READ
        })
      );
    },
    [dispatch]
  );

  const onActiveControlSelected = useCallback(
    row => {
      // clear the selected row from the other tables
      expiredControlsTableRef.current.api.clearFocusedCell();
      curvesTableRef.current.api.clearFocusedCell();
      onControlSelected(row);
    },
    [onControlSelected]
  );

  const onExpiredControlSelected = useCallback(
    row => {
      // clear the selected row from the other tables
      activeControlsTableRef.current.api.clearFocusedCell();
      curvesTableRef.current.api.clearFocusedCell();
      onControlSelected(row);
    },
    [onControlSelected]
  );

  const onCurveSelected = useCallback(
    row => {
      // clear the selected row from the other tables
      activeControlsTableRef.current.api.clearFocusedCell();
      expiredControlsTableRef.current.api.clearFocusedCell();

      const { curveId } = row.data;
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.CURVE,
          id: curveId,
          state: SIDEPANEL_STATES.READ
        })
      );
    },
    [dispatch]
  );

  const openCreatePanel = async (e, panelType) => {
    e.preventDefault();
    await dispatch(closeSidePanel());
    dispatch(
      setSidePanel({ entity: panelType, state: SIDEPANEL_STATES.CREATE })
    );
    setShowSubmenuDropdown(false);
  };

  const toggleMenu = () => {
    if (showAcctMenuDropdown) {
      dispatch(toggleAccountMenuDropdown(false));
    }
    if (showSubmenuDropdown) {
      setShowSubmenuDropdown(false);
    }
  };

  const getRowHeight = ({ data }) => {
    const { controlTypes } = data;

    const controlTypeKeys = Object.keys(controlTypes);
    if (controlTypeKeys) {
      if (controlTypeKeys.length > defaultItemsToDisplayInSingleRow) {
        return (
          defaultRowHeight +
          defaultSecondaryRowHeight * defaultItemsToDisplayInSingleRow
        );
      }
      return (
        defaultRowHeight +
        defaultSecondaryRowHeight * (controlTypeKeys.length - 1)
      );
    }
    return defaultRowHeight;
  };

  // If the sidepanel closes, deselect any selected items on the table
  useEffect(() => {
    if (
      activeControlsTableRef.current?.api &&
      expiredControlsTableRef.current?.api &&
      curvesTableRef.current?.api &&
      !sidePanelIsOpen
    ) {
      activeControlsTableRef.current.api.clearFocusedCell();
      expiredControlsTableRef.current.api.clearFocusedCell();
      curvesTableRef.current.api.clearFocusedCell();
    }
  }, [sidePanelIsOpen]);

  return (
    <FlexPage>
      {isLoading ? (
        <Spinner message="Loading Controls..." />
      ) : (
        <ControlsPage onClick={toggleMenu}>
          <Submenu
            dropdownIsShowing={showSubmenuDropdown}
            selectOption={openCreatePanel}
            toggleDropdownVisibility={isShowing =>
              setShowSubmenuDropdown(isShowing)
            }
            submenuType={SUBMENU_TYPES.TOPOLOGY}
          />
          <FlexPage>
            <Table
              ref={activeControlsTableRef}
              onRowClicked={onActiveControlSelected}
              columnDefs={controlColumnDefs()}
              rowData={controlsReadableData}
              bigTitle="Current Controls"
              height="450px"
              width="1100px"
              getRowHeight={getRowHeight}
            />
            <Table
              ref={expiredControlsTableRef}
              onRowClicked={onExpiredControlSelected}
              columnDefs={controlColumnDefs(true)}
              rowData={deletedControlsReadableData}
              bigTitle="Expired Controls"
              height="450px"
              width="1100px"
              getRowHeight={getRowHeight}
            />
            <Table
              ref={curvesTableRef}
              onRowClicked={onCurveSelected}
              columnDefs={curveColumnDefs()}
              rowData={curvesReadableData}
              bigTitle="All Curves"
              height="450px"
              width="450px"
              getRowHeight={() => 60}
            />
          </FlexPage>
        </ControlsPage>
      )}
    </FlexPage>
  );
};

export default Controls;
